import { useMemo } from 'react';
import { useSelf } from 'App/components/hooks/appHooks';
import { PageIframeInit } from './PageIframe';
import AppService from 'App/legacy/AppService';

/**
 * Builds the `init` payload posted into a sandboxed Page iframe from the
 * current session. `clientId` scopes the page to a company so its
 * `useAccount()` hook resolves; `orderId` scopes it to an order so `useOrder()`
 * resolves. Pass `null` for either when the host isn't scoped to that entity.
 *
 * Shared by every Page host (standalone `PageView`, the company-card embed,
 * the order-modal embed, the authoring preview) so the payload shape stays in
 * one place.
 */
export function usePageIframeInit(clientId: number | null, orderId: number | null = null): PageIframeInit {
	const self = useSelf();
	const language: string | null = (self as { language?: string } | null)?.language ?? null;
	// Tenant id — the `:customerId` segment of every in-app hash route. Resolved
	// the same way `openCrmEntity` does so Page deep links match the host's URLs.
	const rawCustomerId = AppService.getCustomerId();
	const customerId: number | null = rawCustomerId != null ? Number(rawCustomerId) : null;

	return useMemo<PageIframeInit>(
		() => ({
			userId: self?.id ?? null,
			customerId,
			clientId,
			orderId,
			language,
			currentRoute: typeof window !== 'undefined' ? window.location.hash || null : null
		}),
		[self?.id, customerId, language, clientId, orderId]
	);
}
